import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCompanies } from "../features/companiesSlice"; // Adjust path
import Select, { components } from "react-select";
import {
  onAuthStateChanged,
  signOut,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import { auth } from "../firebase"; // Adjust path
import SignInModal from "./SignInModal"; // Adjust path
import "./CompaniesListMobile.css"; // Import the MOBILE CSS file
import { createPortal } from 'react-dom';

// --- Helper Functions for Metric Styling ---
// ... (keep existing helper functions)
const getExitRiskClass = (risk) => {
    const lowerRisk = String(risk || 'N/A').toLowerCase().trim();
    switch (lowerRisk) {
      case 'low': return 'exit-risk-low';
      case 'medium': case 'moderate': return 'exit-risk-medium';
      case 'high': return 'exit-risk-high';
      default: return 'exit-risk-na';
    }
  };
  
  const getRatingClass = (rating) => {
    const normalizedRating = String(rating || 'N/A').toUpperCase().trim();
    if (normalizedRating === 'N/A' || normalizedRating === '') return 'rating-na';
    const grade = normalizedRating.charAt(0);
    switch (grade) {
      case 'A': case 'B': return 'rating-green';
      case 'C': return 'rating-orange';
      case 'D': case 'F': return 'rating-red';
      default:
        if (normalizedRating === 'POOR') return 'rating-red';
        return 'rating-na';
    }
  };
// --- End Helper Functions ---

function CompaniesListMobile() {
  const dispatch = useDispatch();
  const { companies, loading, error } = useSelector((state) => state.companies);

  // State variables
  const [user, setUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Filter States
  const [selectedNames, setSelectedNames] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [selectedInvestors, setSelectedInvestors] = useState([]);
  const [selectedStage, setSelectedStage] = useState([]);
  const [selectedType, setSelectedType] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedExitRisk, setSelectedExitRisk] = useState([]);
  const [selectedRating, setSelectedRating] = useState([]);

  // Update sort state to include 'unsorted'
  const [employeesSort, setEmployeesSort] = useState('unsorted');
  const [ratingSort, setRatingSort] = useState('unsorted');

  // Refs
  const userMenuRef = useRef(null); // For menu itself
  const userControlRef = useRef(null); // For the control area containing avatar
  const filterToggleButtonRef = useRef(null);
  const filterModalRef = useRef(null);

  // --- Hooks ---

  // Select Styles (Optimized for modal usage)
  const selectStyles = useMemo(() => ({ // Wrap in useMemo if needed, though usually static
    control: (base) => ({
      // ... keep existing control styles
      ...base,
      minHeight: '40px',
      height: '40px',
      borderRadius: '4px',
      border: '1px solid var(--border-color)',
      backgroundColor: 'var(--white)',
      boxShadow: 'none',
      '&:hover': {
        borderColor: 'var(--primary-blue)'
      }
    }),
    valueContainer: (base) => ({
      // ... keep existing valueContainer styles
        ...base,
        height: '40px',
        padding: '0 8px',
        overflow: 'hidden',
        flexWrap: 'nowrap'
    }),
    multiValue: (base) => ({
       // ... keep existing multiValue styles
        ...base,
        backgroundColor: 'var(--light-gray)',
        borderRadius: '4px',
        margin: '2px 3px',
        maxWidth: 'calc(100% - 6px)'
    }),
    multiValueLabel: (base) => ({
       // ... keep existing multiValueLabel styles
        ...base,
        color: 'var(--dark-gray)',
        padding: '2px 6px',
        fontSize: '0.9em',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    }),
    multiValueRemove: (base) => ({
        // ... keep existing multiValueRemove styles
        ...base,
        color: 'var(--medium-gray)',
        ':hover': {
          backgroundColor: 'var(--border-color)',
          color: 'var(--dark-gray)'
        }
    }),
    option: (base, state) => ({
        // ... keep existing option styles
        ...base,
        backgroundColor: state.isSelected
          ? 'var(--primary-blue)'
          : state.isFocused
            ? 'var(--light-gray)'
            : 'var(--white)',
        color: state.isSelected ? 'var(--white)' : 'var(--dark-gray)',
        cursor: 'pointer',
        padding: '8px 12px',
        ':active': {
          backgroundColor: state.isSelected
            ? 'var(--primary-blue)'
            : 'var(--light-gray)',
          color: state.isSelected ? 'var(--white)' : 'var(--dark-gray)'
        },
        ':hover': {
          backgroundColor: state.isSelected
            ? 'var(--primary-blue)'
            : 'var(--light-gray)',
          color: state.isSelected ? 'var(--white)' : 'var(--dark-gray)'
        }
    }),
    menu: (base) => ({
        // ... keep existing menu styles
        ...base,
        zIndex: 10101, // Ensure it's above modal content if not using portal
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        borderRadius: '4px',
        marginTop: base.placement === 'bottom' ? '4px' : '0', // Optional: Adjust spacing based on placement
        marginBottom: base.placement === 'top' ? '4px' : '0'   // Optional: Adjust spacing based on placement
    }),
    menuPortal: base => ({
      // Keep z-index for portal high
      ...base,
      zIndex: 10101
    })
  }), []); // Empty dependency array if styles are static

  // Calculate Active Filter Count
  const activeFilterCount = useMemo(() => {
    const filters = [selectedNames, selectedIndustry, selectedLocation, selectedInvestors, selectedStage, selectedType, selectedEmployees, selectedExitRisk, selectedRating,];
    return filters.reduce((count, filterArray) => count + (filterArray.length > 0 ? 1 : 0), 0);
  }, [selectedNames, selectedIndustry, selectedLocation, selectedInvestors, selectedStage, selectedType, selectedEmployees, selectedExitRisk, selectedRating,]);

  // Filter Options & Memoization
  const sortOptions = useMemo(() => (arr) => {
    // ... (keep existing sortOptions logic)
    const sortRatings = (a, b) => {
        if (a === 'N/A' || b === 'N/A') return a === 'N/A' ? 1 : -1;
        const gradeOrder = { 'A+': 1, 'A': 2, 'A-': 3, 'B+': 4, 'B': 5, 'B-': 6, 'C+': 7, 'C': 8, 'C-': 9, 'D+': 10, 'D': 11, 'D-': 12, 'F': 13 };
        return (gradeOrder[a] || 99) - (gradeOrder[b] || 99);
      };
  
      return [...new Set(arr)]
        .filter(val => val !== null && val !== undefined && String(val).trim() !== '')
        .map(i => ({ value: String(i), label: String(i) }))
        .sort((a, b) => {
          // Special sorting for ratings
          if (a.value.match(/^[A-F][+-]?$/) || b.value.match(/^[A-F][+-]?$/)) {
            return sortRatings(a.value, b.value);
          }
          // General locale comparison
          return a.label.localeCompare(b.label);
        });
  }, []);

  const uniqueOptions = useMemo(() => {
    if (!Array.isArray(companies) || companies.length === 0) return { names: [], industries: [], locations: [], investors: [], exitRisks: [], ratings: [] };
    const validCompanies = companies.filter(c => c); const allInvestors = validCompanies.flatMap(c => c.investors || []); const uniqueInvestors = [...new Set(allInvestors.map(inv => String(inv || '').trim()).filter(Boolean))];
    return { names: sortOptions(validCompanies.map(c => c.name)), industries: sortOptions(validCompanies.map(c => c.industry)), locations: sortOptions(validCompanies.map(c => c.location)), investors: sortOptions(uniqueInvestors), exitRisks: sortOptions(validCompanies.map(c => c.exit_risk)), ratings: sortOptions(validCompanies.map(c => c.rating)), }
  }, [companies, sortOptions]);

  const { stageOptions, employeeOptions, typeOptions } = useMemo(() => ({
    // ... (keep existing static options logic)
    stageOptions: [
        { value: "Seed", label: "Seed" },
        { value: "Series A", label: "Series A" },
        { value: "Series B", label: "Series B" },
        { value: "Series C", label: "Series C" },
        { value: "Series D", label: "Series D" },
        { value: "Series E or Later", label: "Series E or Later" },
        { value: "N/A", label: "N/A" }
      ].sort((a, b) => a.label.localeCompare(b.label)),
      employeeOptions: [
        { value: "0-50", label: "0–50", sortOrder: 1 },
        { value: "50-100", label: "50–100", sortOrder: 2 },
        { value: "100-250", label: "100–250", sortOrder: 3 },
        { value: "250-500", label: "250–500", sortOrder: 4 },
        { value: "500-1000", label: "500–1k", sortOrder: 5 },
        { value: "1k+", label: "1k+", sortOrder: 6 },
        { value: "N/A", label: "N/A", sortOrder: 7 }
      ].sort((a, b) => a.sortOrder - b.sortOrder),
      typeOptions: [
        { value: "B2B", label: "B2B" },
        { value: "B2C", label: "B2C" },
        { value: "N/A", label: "N/A" }
      ].sort((a, b) => a.label.localeCompare(b.label)),
  }), []);

  // Update sort functions to handle unsorted state
  const sortEmployees = (a, b) => {
    // ... (keep existing sort logic)
    if (employeesSort === 'unsorted') return 0;
    const order = employeesSort === 'asc' ? 1 : -1;
    if (a.value === 'N/A') return 1;
    if (b.value === 'N/A') return -1;
    return (a.sortOrder - b.sortOrder) * order;
  };

  const sortRatings = (a, b) => {
    // ... (keep existing sort logic)
    if (ratingSort === 'unsorted') return 0;
    const order = ratingSort === 'asc' ? 1 : -1;
    if (a.value === 'N/A' || b.value === 'N/A') return a.value === 'N/A' ? 1 : -1;
    const gradeOrder = { 'A+': 1, 'A': 2, 'A-': 3, 'B+': 4, 'B': 5, 'B-': 6, 'C+': 7, 'C': 8, 'C-': 9, 'D+': 10, 'D': 11, 'D-': 12, 'F': 13 };
    return ((gradeOrder[a.value] || 99) - (gradeOrder[b.value] || 99)) * order;
  };

  // Update the employeeOptions and ratings sorting
  const sortedEmployeeOptions = useMemo(() =>
    employeeOptions.slice().sort(sortEmployees)
  , [employeeOptions, employeesSort]);

  const sortedRatingOptions = useMemo(() =>
    uniqueOptions.ratings.slice().sort(sortRatings)
  , [uniqueOptions.ratings, ratingSort]);

  // Filtering Logic
  const filteredCompanies = useMemo(() => {
    // ... (keep existing filtering logic)
    const companyList = Array.isArray(companies) ? companies : []; if (activeFilterCount === 0) return companyList;
    return companyList.filter(company => {
      if (!company) return false;
      const matchesSelection = (value, selectedOptions) => { if (selectedOptions.length === 0) return true; const currentVal = String(value ?? "N/A").trim(); return selectedOptions.some(sel => String(sel.value).trim() === currentVal); };
      const matchesMultiSelection = (values, selectedOptions) => { if (selectedOptions.length === 0) return true; const currentValues = Array.isArray(values) ? values.map(v => String(v ?? '').trim()).filter(Boolean) : []; if (currentValues.length === 0 && selectedOptions.some(sel => sel.value === "N/A")) return true; if (currentValues.length === 0) return false; return selectedOptions.some(sel => currentValues.includes(String(sel.value).trim())); };
      return matchesSelection(company.name, selectedNames) && matchesSelection(company.industry, selectedIndustry) && matchesSelection(company.location, selectedLocation) && matchesSelection(company.stage, selectedStage) && matchesSelection(company.type, selectedType) && matchesSelection(company.employees, selectedEmployees) && matchesSelection(company.exit_risk, selectedExitRisk) && matchesSelection(company.rating, selectedRating) && matchesMultiSelection(company.investors, selectedInvestors);
    });
  }, [companies, activeFilterCount, selectedNames, selectedIndustry, selectedLocation, selectedStage, selectedType, selectedEmployees, selectedInvestors, selectedExitRisk, selectedRating]);

  // Common Select Props for Modal Filters
  const commonSelectProps = useMemo(() => ({
    styles: selectStyles,
    isMulti: true,
    className: "mobile-select",
    classNamePrefix: "react-select",
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    menuPlacement: 'auto', // <--- ADD THIS LINE
    closeMenuOnSelect: false,
    hideSelectedOptions: false,
    blurInputOnSelect: false,
    isSearchable: true,
    components: {
      MultiValueContainer: ({ children, ...props }) => {
          // ... keep existing MultiValueContainer logic
          const { selectProps, data } = props;
          const values = selectProps.value;
          const index = values.findIndex(v => v.value === data.value);
          
          // Only show the first two values
          if (index > 1) return null;
          
          // If this is the second value and there are more, add the "+X more" pill
          if (index === 1 && values.length > 2) {
            return (
              <>
                <components.MultiValueContainer {...props}>
                  {children}
                </components.MultiValueContainer>
                <div className="react-select__multi-value--more">
                  +{values.length - 2} more
                </div>
              </>
            );
          }
          
          return <components.MultiValueContainer {...props}>{children}</components.MultiValueContainer>;
      }
    }
  }), [selectStyles]); // Dependency array only needs selectStyles if it's dynamic

  // --- Effects ---
  useEffect(() => { // Auth Link Check
    // ... (keep existing effect)
    if (typeof window !== 'undefined' && !isAuthLoading && isSignInWithEmailLink(auth, window.location.href)) {
        let email = window.localStorage.getItem('emailForSignIn'); if (!email) { email = window.prompt('Please provide your email to complete sign-in:'); }
        if (email) { signInWithEmailLink(auth, email, window.location.href).then((result) => { console.log("Signed in via email link:", result.user); window.localStorage.removeItem('emailForSignIn'); window.history.replaceState(null, document.title, window.location.pathname + window.location.search); }).catch((err) => { console.error("Error signing in with email link:", err); window.localStorage.removeItem('emailForSignIn'); window.history.replaceState(null, document.title, window.location.pathname + window.location.search); alert(`Error signing in: ${err.message}. Please try signing in manually.`); }); }
        else { alert("Email is required to complete sign-in via link."); window.history.replaceState(null, document.title, window.location.pathname + window.location.search); }
      }
  }, [isAuthLoading]);

  useEffect(() => { // Auth State Listener
    // ... (keep existing effect)
    setIsAuthLoading(true);
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      console.log("Mobile Auth Listener - User:", currentUser); // DEBUG LOG
      setUser(currentUser); setIsAuthLoading(false);
      if (currentUser) { setShowSignInModal(false); } else { setShowUserMenu(false); }
    }); return () => unsubscribe();
  }, []);

  useEffect(() => { // Fetch Companies Data
    dispatch(fetchCompanies());
  }, [dispatch]);

  useEffect(() => { // Click Outside User Menu Listener
    // ... (keep existing effect)
    function handleClickOutside(event) {
        // Check if click is outside the menu AND outside the control area that opens it
        if (userMenuRef.current && !userMenuRef.current.contains(event.target) &&
            userControlRef.current && !userControlRef.current.contains(event.target)) {
          setShowUserMenu(false);
        }
      }
      if (showUserMenu) { document.addEventListener("mousedown", handleClickOutside); }
      else { document.removeEventListener("mousedown", handleClickOutside); }
      return () => { document.removeEventListener("mousedown", handleClickOutside); };
  }, [showUserMenu]);

  useEffect(() => { // Effect for Modal Focus Management & Body Scroll Lock
     // ... (keep existing effect)
     const handleKeyDown = (event) => {
        if (event.key === 'Escape') { closeFilterModal(); }
        if (event.key === 'Tab' && filterModalRef.current) {
          const focusableElements = filterModalRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'); if (!focusableElements || focusableElements.length === 0) return; const firstElement = focusableElements[0]; const lastElement = focusableElements[focusableElements.length - 1];
          if (event.shiftKey) { if (document.activeElement === firstElement) { lastElement.focus(); event.preventDefault(); } }
          else { if (document.activeElement === lastElement) { firstElement.focus(); event.preventDefault(); } }
        }
      };
      if (isFilterModalOpen) { document.body.style.overflow = 'hidden'; document.addEventListener('keydown', handleKeyDown); setTimeout(() => { const closeButton = filterModalRef.current?.querySelector('.filter-modal-close-button'); closeButton?.focus(); }, 100); }
      else { document.body.style.overflow = ''; document.removeEventListener('keydown', handleKeyDown); }
      return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', handleKeyDown); };
  }, [isFilterModalOpen]); // Re-run when modal state changes


  // --- Helper Functions ---
  const getInitials = (displayName, email) => {
     // ... (keep existing function)
     const name = displayName || email || ""; if (!name) return "?"; if (name.includes('@') && !displayName) { return name[0].toUpperCase(); } const parts = name.split(" ").filter(Boolean); if (parts.length === 0) return "?"; const firstInitial = parts[0][0]?.toUpperCase() ?? ""; const lastInitial = parts.length > 1 ? parts[parts.length - 1][0]?.toUpperCase() ?? "" : ""; return firstInitial + lastInitial || "?";
  };
  const handleLogout = async () => {
    // ... (keep existing function)
    setShowUserMenu(false); try { await signOut(auth); } catch (err) { console.error("Sign out error:", err); alert("Error signing out."); } 
  };
  const handleCompanyClick = (companyName) => {
     // ... (keep existing function)
     if (!companyName) return; 
     const safeName = companyName.toLowerCase().replace(/[^a-z0-9\s-]/gi, '').trim().replace(/\s+/g, '-'); 
     if (safeName) { 
       window.location.href = `/${safeName}-summary.html`; 
     } else { 
       console.warn("Could not generate valid URL slug:", companyName); 
     } 
  };
  const handleViewReport = (company) => {
     // ... (keep existing function)
     if (!company || !company.name) return;
     const safeName = company.name.toLowerCase().replace(/[^a-z0-9\s-]/gi, '').trim().replace(/\s+/g, '-');
     if (safeName) {
       window.location.href = `/${safeName}-summary.html`;
     } else {
       console.warn("Could not generate valid URL slug:", company.name);
     }
  };
  const clearAllFilters = () => { setSelectedNames([]); setSelectedIndustry([]); setSelectedLocation([]); setSelectedInvestors([]); setSelectedStage([]); setSelectedType([]); setSelectedEmployees([]); setSelectedExitRisk([]); setSelectedRating([]); };
  const closeFilterModal = useCallback(() => { setIsFilterModalOpen(false); filterToggleButtonRef.current?.focus(); }, []);

  // --- Render Logic ---

  console.log("Mobile Header Render Check - isAuthLoading:", isAuthLoading, "User:", user);

  if (isAuthLoading && !user) { // Show full screen loading only on initial load without user
     return <div className="mobile-layout-container"><div className="loading-container"><div>Loading...</div></div></div>;
  }
  // If error, show error state but still render header if possible
  if (error) {
    // ... (keep existing error rendering logic)
    const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);
    return (
       <div className="mobile-layout-container">
         <header className="mobile-header">
           <div className="app-branding"><a href="/" className="app-logo-link"><img src="/logo.png" alt="Logo" className="app-logo" /></a></div>
           {/* Still show sign-in button or loading state in header during error */}
           <div className="user-controls-container">
                {isAuthLoading ? <span>Loading...</span> : (
                    !user && <button className="header-signin" onClick={() => setShowSignInModal(true)}>Sign In</button>
                )}
           </div>
         </header>
         <div className="mobile-scrollable-content" style={{paddingTop: '0'}}> {/* Remove padding top for error */}
             <div className="error-container">
               <h2>Error Loading Companies</h2>
               <p>Could not retrieve company data. Check connection and try again.</p>
               <p><i><small>Details: {errorMessage}</small></i></p>
               <button onClick={() => dispatch(fetchCompanies())}>Try Again</button>
             </div>
         </div>
       </div>
     );
  }

  // Main Component Render
  return (
    <div className="mobile-layout-container">
      {/* Mobile Header */}
      <header className="mobile-header">
         {/* ... Keep header content */}
         <div className="app-branding">
          <a href="/" className="app-logo-link">
            <img src="/logo.png" alt="Logo" className="app-logo" />
          </a>
        </div>

        {/* User Controls */}
        <div className="user-controls-container">
          {isAuthLoading ? (
            <div className="loading-placeholder" style={{ color: 'white', padding: '10px 15px' }}>Loading...</div>
          ) : user ? (
            <div ref={userControlRef} className="user-control-area" onClick={() => setShowUserMenu(!showUserMenu)}>
              <div className="user-avatar">{getInitials(user.displayName, user.email)}</div>
              {showUserMenu && createPortal(
                <div className="user-menu-portal">
                  <div className="user-menu" ref={userMenuRef}>
                    <div className="user-menu-email">{user.email}</div>
                    <button onClick={handleLogout}>Logout</button>
                  </div>
                </div>,
                document.body
              )}
            </div>
          ) : (
            <button className="header-signin" onClick={() => setShowSignInModal(true)}>Sign In</button>
          )}
        </div>
      </header>

      {/* Filters Bar (Sticky) */}
      <div className="mobile-filters-container">
        <div className="company-name-filter">
          <Select
            isMulti
            options={uniqueOptions.names}
            value={selectedNames}
            onChange={setSelectedNames}
            placeholder="Search companies..."
            styles={{...selectStyles, // Use spread to inherit base styles
                control: (base, state) => ({
                  ...selectStyles.control(base, state), // Keep base control styles
                  minHeight: '40px', // Specific height for filter bar
                  height: '40px'
                }),
                valueContainer: (base, state) => ({
                   ...selectStyles.valueContainer(base, state),
                   height: '40px'
                }),
                indicatorsContainer: (base) => ({ // Ensure indicators fit
                   ...base,
                   height: '40px'
                })
             }}
            maxMenuHeight={200}
            menuPlacement="auto" // <--- ADD THIS LINE
            isClearable={false}
            hideSelectedOptions={false}
            closeMenuOnSelect={false}
            blurInputOnSelect={false}
            isSearchable={true}
            className="mobile-filter-bar-select" // Optional: add specific class if needed
            classNamePrefix="react-select"
            components={{
              MultiValueContainer: ({ children, ...props }) => {
                 // ... keep existing MultiValueContainer logic for filter bar
                 const { selectProps, data } = props;
                const values = selectProps.value;
                const index = values.findIndex(v => v.value === data.value);
                
                // Only show the first two values
                if (index > 1) return null;
                
                // If this is the second value and there are more, add the "+X more" pill
                if (index === 1 && values.length > 2) {
                  return (
                    <>
                      <components.MultiValueContainer {...props}>
                        {children}
                      </components.MultiValueContainer>
                      <div className="react-select__multi-value--more">
                        +{values.length - 2} more
                      </div>
                    </>
                  );
                }
                
                return <components.MultiValueContainer {...props}>{children}</components.MultiValueContainer>;
              }
            }}
          />
        </div>
        <button
          ref={filterToggleButtonRef} className="filter-toggle-button" onClick={() => setIsFilterModalOpen(true)} aria-label={`Filters ${activeFilterCount > 0 ? `(${activeFilterCount} active)` : ''}`} aria-haspopup="dialog" aria-expanded={isFilterModalOpen} aria-controls="filter-modal"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="filter-icon"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
          {!isFilterModalOpen && activeFilterCount > 0 && ( <span className="filter-count-badge" aria-hidden="true">{activeFilterCount}</span> )}
        </button>
      </div>

      {/* Scrollable Content Area */}
      <div className="mobile-scrollable-content">
         {/* ... Keep loading/content logic */}
         {loading && companies.length === 0 && !error && (
             <div className="loading-container"><div>Loading Companies...</div></div>
        )}

        {/* Companies List */}
        {!loading || companies.length > 0 ? (
          <div className="mobile-companies-list">
            {filteredCompanies.length > 0 ? (
              filteredCompanies.map((company, index) => {
                // ... keep company card rendering logic
                const { id, name = "N/A", exit_risk = "N/A", rating = "N/A", stage = "N/A", employees = "N/A", industry = "N/A", location = "N/A", type = "N/A" } = company || {};
                const key = id || `${name}-${index}`; const shouldBlur = !user && index >= 2;
                return (
                  <div key={key} className={`company-card ${shouldBlur ? 'blurred' : ''}`}>
                    <div className="company-card-header">
                      <h3 className="company-name">{name}</h3>
                      <div className="company-metrics">
                        {exit_risk !== "N/A" && <span className={`metric ${getExitRiskClass(exit_risk)}`}>Exit Risk: {exit_risk}</span>}
                        {rating !== "N/A" && <span className={`metric ${getRatingClass(rating)}`}>Rating: {rating}</span>}
                      </div>
                    </div>
                    <div className="company-details">
                      <div className="detail-row">
                        <span className="detail-label">Stage</span>
                        <span className="detail-value">{stage || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Industry</span>
                        <span className="detail-value">{industry || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Location</span>
                        <span className="detail-value">{location || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Employees</span>
                        <span className="detail-value">{employees || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Type</span>
                        <span className="detail-value">{type || 'N/A'}</span>
                      </div>
                       <div className="detail-row">
                        <button
                          className="view-report-button"
                          onClick={(e) => { e.stopPropagation(); handleViewReport(company); }}
                          aria-label={`View report for ${name}`}
                          disabled={shouldBlur}
                        >
                          View Report
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : ( // No results condition
              !loading && <div className="no-results"> {activeFilterCount > 0 ? ( <p>No companies match the current filters.</p> ) : ( <p>No companies found.</p> )} </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Signup prompt */}
      {!isAuthLoading && !user && (
         // ... Keep signup prompt
         <div className="mobile-signup-prompt"> <p>Sign in or create account to continue browsing.</p> <p className="signup-note">Summary reports are 100% free.</p> <button className="sign-in-button" onClick={() => setShowSignInModal(true)}>Sign In</button> </div>
      )}

      {/* --- Filter Modal --- */}
      {isFilterModalOpen && (
        <div className="filter-modal-overlay" onClick={closeFilterModal} role="presentation">
          {/* Portal is handled by react-select's menuPortalTarget */}
          <div ref={filterModalRef} className="filter-modal" id="filter-modal" role="dialog" aria-modal="true" aria-labelledby="filter-modal-title" onClick={(e) => e.stopPropagation()} >
            <div className="filter-modal-header"> <h2 id="filter-modal-title" className="filter-modal-title">Filters</h2> <button className="filter-modal-close-button" onClick={closeFilterModal} aria-label="Close Filters Modal">×</button> </div>
            <div className="filter-modal-content">
              {/* Use commonSelectProps which now includes menuPlacement: 'auto' */}
              <div className="filter-item"><label htmlFor="industry-select">Industry</label><Select {...commonSelectProps} inputId="industry-select" aria-label="Industry Filter" placeholder="Select industries..." options={uniqueOptions.industries} value={selectedIndustry} onChange={setSelectedIndustry} noOptionsMessage={() => "No industries found"} /></div>
              <div className="filter-item"><label htmlFor="location-select">Location</label><Select {...commonSelectProps} inputId="location-select" aria-label="Location Filter" placeholder="Select locations..." options={uniqueOptions.locations} value={selectedLocation} onChange={setSelectedLocation} noOptionsMessage={() => "No locations found"}/></div>
              <div className="filter-item"><label htmlFor="investors-select">Investors</label><Select {...commonSelectProps} inputId="investors-select" aria-label="Investors Filter" placeholder="Select investors..." options={uniqueOptions.investors} value={selectedInvestors} onChange={setSelectedInvestors} noOptionsMessage={() => "No investors found"}/></div>
              <div className="filter-item"><label htmlFor="stage-select">Stage</label><Select {...commonSelectProps} inputId="stage-select" aria-label="Stage Filter" placeholder="Select stages..." options={stageOptions} value={selectedStage} onChange={setSelectedStage}/></div>
              <div className="filter-item"><label htmlFor="type-select">Type</label><Select {...commonSelectProps} inputId="type-select" aria-label="Type Filter" placeholder="Select types..." options={typeOptions} value={selectedType} onChange={setSelectedType}/></div>
              <div className="filter-item">
                <label htmlFor="employees-select">Employees</label>
                <Select
                  {...commonSelectProps}
                  inputId="employees-select"
                  aria-label="Employees Filter"
                  placeholder="Select employee ranges..."
                  options={employeeOptions} // Use unsorted options here
                  value={selectedEmployees}
                  onChange={setSelectedEmployees}
                />
              </div>
              <div className="filter-item"><label htmlFor="exit-risk-select">Exit Risk</label><Select {...commonSelectProps} inputId="exit-risk-select" aria-label="Exit Risk Filter" placeholder="Select exit risks..." options={uniqueOptions.exitRisks} value={selectedExitRisk} onChange={setSelectedExitRisk}/></div>
              <div className="filter-item">
                <label htmlFor="rating-select">Rating</label>
                <Select
                  {...commonSelectProps}
                  inputId="rating-select"
                  aria-label="Rating Filter"
                  placeholder="Select ratings..."
                  options={uniqueOptions.ratings} // Use unsorted options here
                  value={selectedRating}
                  onChange={setSelectedRating}
                />
              </div>
            </div>
            <div className="filter-modal-footer"> <button className="modal-clear-button" onClick={clearAllFilters} disabled={activeFilterCount === 0}>Clear All ({activeFilterCount})</button> <button className="modal-action-button" onClick={closeFilterModal}>Done</button> </div>
          </div>
        </div>
      )}

      {/* Sign In Modal */}
      {showSignInModal && <SignInModal onClose={() => setShowSignInModal(false)} />}
    </div>
  );
}

export default CompaniesListMobile;